/**
 * Lista que chega aos pedaços — a página seguinte vem quando a rolagem pede.
 *
 * O que existia antes era teto fixo e silêncio: a caixa do WhatsApp cortava
 * 892 conversas em 300, a agenda cortava 2.561 contatos em 300, e a mesa
 * escondia os 528 casos da planilha atrás de `TETO_DA_MESA`. Ninguém na tela
 * ficava sabendo — a lista simplesmente acabava.
 *
 * As quatro coisas que fazem isto ser confiável, e não só "carregar mais":
 *
 *   - **Corrida de pedidos.** Quem digita rápido na busca dispara um pedido por
 *     tecla. O anterior é cortado (`AbortController`) e a resposta atrasada é
 *     descartada pela senha da rodada: a lista mostra o que foi digitado por
 *     último, nunca o que voltou por último.
 *   - **Repetido nunca entra duas vezes.** Paginar por `offset` numa lista
 *     ordenada por última mensagem: chega mensagem nova, a ordem muda, e a
 *     página 2 devolve alguém que já estava na página 1. A junção é por chave.
 *   - **Erro no meio não apaga o que já veio.** Falhou a página 3, as duas
 *     primeiras continuam na tela com o motivo do lado e o botão de tentar.
 *   - **Evento do servidor mexe no topo, não na cauda.** Quem rolou 400 linhas
 *     não volta para o começo porque alguém mandou uma mensagem: a primeira
 *     página é relida e casada por chave, o resto fica onde está. É o
 *     "recarregue a trilha, nunca o rascunho" aplicado à rolagem.
 */
import { useCallback, useEffect, useRef, useState } from "react";

/** Identidade padrão de um item entre páginas. */
const PELO_ID = (x) => x.id;

/** Junta mantendo a ordem de chegada e a primeira aparição de cada chave. */
function juntar(anteriores, novos, chaveDe) {
  const vistos = new Set(anteriores.map(chaveDe));
  const saem = [...anteriores];
  for (const item of novos) {
    const k = chaveDe(item);
    if (vistos.has(k)) continue;
    vistos.add(k);
    saem.push(item);
  }
  return saem;
}

/**
 * @param buscar  `(offset, limite, sinal) => Promise<{ itens, total }>`.
 *
 *   **Contrato:** `buscar` precisa vir de um `useCallback` cujas dependências
 *   sejam o RECORTE (busca, filtro, pasta…). A identidade dela é o que diz
 *   "isto é outra lista": mudou, a rolagem recomeça do zero. Função criada solta
 *   no corpo do componente recomeça a lista a cada render — o `exhaustive-deps`
 *   do lint é o que cobra isso de quem usa.
 *
 * @param chaveDe o que identifica um item entre páginas (id, jid, chave…);
 *                também precisa ser estável.
 * @param tamanho quantos por página.
 */
export function usePaginacao({ buscar, chaveDe = PELO_ID, tamanho = 60 }) {
  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  /* A senha da rodada: resposta de recorte antigo chega e é jogada fora. */
  const senha = useRef(0);
  const corte = useRef(null);
  const emVoo = useRef(false);
  /* De onde pedir a próxima página. Contado a partir do que o servidor JÁ
     entregou, e não do tamanho da lista na tela: com a junção por chave os dois
     números divergem, e é o do servidor que diz onde a página seguinte começa. */
  const entregues = useRef(0);

  const pedir = useCallback(
    async (offset, recomecar) => {
      if (emVoo.current && !recomecar) return;
      if (recomecar) {
        corte.current?.abort();
        senha.current += 1;
        entregues.current = 0;
      }
      const minha = senha.current;
      const controle = new AbortController();
      corte.current = controle;
      emVoo.current = true;
      setCarregando(true);
      setErro(null);
      try {
        const r = await buscar(offset, tamanho, controle.signal);
        if (minha !== senha.current) return;
        const chegaram = r?.itens ?? [];
        entregues.current = offset + chegaram.length;
        setTotal(r?.total ?? null);
        setItens((antes) => (recomecar ? chegaram : juntar(antes, chegaram, chaveDe)));
      } catch (e) {
        if (e?.name === "AbortError" || minha !== senha.current) return;
        setErro(e?.message ?? "Falha ao carregar a lista");
      } finally {
        if (minha === senha.current) {
          emVoo.current = false;
          setCarregando(false);
        }
      }
    },
    [buscar, chaveDe, tamanho],
  );

  /* Recorte novo recomeça a lista. O atraso junta as teclas de quem digita
     rápido num pedido só — sem ele, "angelica" são oito idas ao servidor. */
  useEffect(() => {
    const relogio = setTimeout(() => pedir(0, true), 140);
    return () => clearTimeout(relogio);
  }, [pedir]);

  useEffect(() => () => corte.current?.abort(), []);

  /** A próxima página. Chamada pela rolagem; ignora chamada repetida. */
  const mais = useCallback(() => {
    if (!emVoo.current) pedir(entregues.current, false);
  }, [pedir]);

  /** Tentar de novo depois de erro, do ponto onde parou. */
  const tentarDeNovo = useCallback(() => {
    emVoo.current = false;
    pedir(entregues.current, false);
  }, [pedir]);

  /**
   * Relê a primeira página e ela MANDA no começo da lista: a ordem e o que está
   * nela são os do servidor. O que veio de páginas seguintes (quem rolou) fica
   * atrás, sem o que subiu para a primeira.
   *
   * Era "troca no lugar, novo na frente": a conversa que recebia mensagem não
   * subia (a mesa saía da ordem de hora) e o caso que saía do recorte — trocou
   * de responsável com o filtro dela ligado — ficava na tela até o F5 (14/10).
   *
   * É o que o canal de eventos chama. Falha em silêncio de propósito: perder uma
   * atualização de fundo é menos grave que esvaziar a lista de quem está lendo.
   */
  const atualizarTopo = useCallback(async () => {
    const minha = senha.current;
    try {
      const r = await buscar(0, tamanho);
      if (minha !== senha.current) return;
      setTotal(r?.total ?? null);
      setItens((antes) => {
        const topo = r?.itens ?? [];
        const noTopo = new Set(topo.map(chaveDe));
        return [...topo, ...antes.slice(tamanho).filter((i) => !noTopo.has(chaveDe(i)))];
      });
    } catch {
      /* silêncio proposital — ver acima */
    }
  }, [buscar, chaveDe, tamanho]);

  /**
   * Tira um item da lista NA HORA, sem esperar releitura.
   *
   * `atualizarTopo` só enxerga a primeira página: o item morto que está além
   * dela fica. Quem SABE que um item morreu (apagou, arquivou, ou recebeu o
   * evento de outro usuário) tira por aqui.
   */
  const remover = useCallback(
    (chave) => {
      setItens((antes) => antes.filter((i) => chaveDe(i) !== String(chave) && chaveDe(i) !== chave));
      setTotal((t) => (t == null ? t : Math.max(0, t - 1)));
    },
    [chaveDe],
  );

  return {
    itens,
    total,
    carregando,
    erro,
    remover,
    /* `total` nulo é rota que ainda não manda `x-crm-total`: nesse caso a última
       página é a que veio menor que o pedido. */
    temMais: total == null ? false : itens.length < total,
    mais,
    tentarDeNovo,
    atualizarTopo,
  };
}
